/**
 * The incremental game sync's client decisions (#259), pure. The app asks the server to analyze the player's new games
 * (POST /analyze-bulk {"mode":"sync"}): Stockfish, the move classification and the tagger, never an LLM. The server
 * picks the window (on or after the newest stored game's day, the 50 newest) and the limits (one every 60 s, none while
 * a job runs); the client only decides when to ask and when the games lists are stale.
 */
import type { BackfillProgress } from "../../hooks/backfillProgress";
import type { BackfillJob, GameSyncResult } from "../../utils/apiConfig";

/** Ask once Clerk has a session and the player has a linked account (the server checks both again). */
export function shouldStartSync(auth: { readonly isSignedIn: boolean; readonly isLinked: boolean }): boolean {
  return auth.isSignedIn && auth.isLinked;
}

/** How often a visible tab asks again (#336): the server's sync_policy.SYNC_INTERVAL. Sam: "getting games from
 *  chess.com as soon as i finish a game"; his 06:04:27 game missed the one sync, 4 s later, and nothing asked again. */
export const SYNC_ASK_EVERY_MS = 60_000;

/** This tab's last ask: for which linked username, and when (epoch ms). */
export interface SyncAsk {
  readonly username: string | null;
  readonly at: number;
}

export interface SyncAskState {
  /** shouldStartSync's answer. */
  readonly start: boolean;
  readonly username: string | null;
  /** document.visibilityState is "visible". */
  readonly visible: boolean;
  /** The user's job (a sync or the Pro backfill) is shown running: the server answers "running" to any ask. */
  readonly jobRunning: boolean;
}

/** `ask`: ask now, then look again in `inMs`. `wait`: look again in `inMs`. `idle`: no timer; the next look is an
 *  event's (the tab shows, the window gets focus, the job ends, sign-in or the username changes). */
export type SyncAskPlan =
  | { readonly kind: "ask"; readonly inMs: number }
  | { readonly kind: "wait"; readonly inMs: number }
  | { readonly kind: "idle" };

/**
 * Whether the app asks for a sync now (#336). The shell calls it on mount, when the tab becomes visible, when the
 * window gets focus, when its own timer fires and when the inputs change; one rule answers them all. A visible tab
 * asks when it hasn't asked yet, when the linked username isn't the one it last asked for (#305: a relink's account
 * syncs at once, the server allows it), or when its last ask is SYNC_ASK_EVERY_MS old. So a tab switch (visibilitychange
 * then focus) asks once, a focus inside the 60 s asks nothing (the server would answer "recent"), and the timer runs
 * only while the tab is visible. Nothing while hidden, signed out, unlinked, or while a job runs.
 */
export function syncAskPlan(last: SyncAsk | null, state: SyncAskState, now: number): SyncAskPlan {
  if (!state.start || !state.visible || state.jobRunning) return { kind: "idle" };
  if (last === null || last.username !== state.username || now - last.at >= SYNC_ASK_EVERY_MS) {
    return { kind: "ask", inMs: SYNC_ASK_EVERY_MS };
  }
  // A clock set back makes the elapsed time negative: wait one interval, not the gap.
  return { kind: "wait", inMs: Math.min(SYNC_ASK_EVERY_MS - (now - last.at), SYNC_ASK_EVERY_MS) };
}

/** A sync job; anything else (older jobs carry no mode) is the history backfill. */
export function isSyncJob(job: BackfillJob | null | undefined): boolean {
  return job?.mode === "sync";
}

/** The server named a job the cached profile doesn't hold: refetch the profile so the banner starts polling it. */
export function activeJobChanged(result: GameSyncResult, cachedJobId: string | null | undefined): boolean {
  return !!result.jobId && result.jobId !== (cachedJobId ?? null);
}

/** Where refreshStep is in the current job: its id, the 10% bucket last refreshed at, and whether it has ended. */
export interface RefreshTrack {
  readonly jobId: string;
  readonly bucket: number;
  readonly settled: boolean;
}

/**
 * Whether this poll should refresh the games lists (the home's, the Library's, Openings', the analyzed ids). Once per
 * job, when a job seen running ends with games analyzed: COMPLETE, or INTERRUPTED (no write for 40 min, #304) with the
 * games it finished stored. A job already over when first seen finished before the lists loaded. A backfill also
 * refreshes at each 10% while it runs, since its games trickle in for an hour; a sync's 50 at most land together at
 * the end. Keyed by job id, so a session that spans two syncs refreshes for both.
 */
export function refreshStep(track: RefreshTrack | null, progress: BackfillProgress): { readonly track: RefreshTrack | null; readonly refresh: boolean } {
  const { job } = progress;
  if (!job) return { track, refresh: false };
  const bucket = progress.percent == null ? 0 : Math.floor(progress.percent / 10);
  if (!track || track.jobId !== job.jobId) {
    return { track: { jobId: job.jobId, bucket, settled: !progress.isRunning }, refresh: false };
  }
  if (track.settled) return { track, refresh: false };
  if (!progress.isRunning) {
    const landed = progress.isComplete ? job.gamesTotal > 0 : job.status === "INTERRUPTED" && job.gamesComplete > 0;
    return { track: { ...track, settled: true }, refresh: landed };
  }
  const crossed = !isSyncJob(job) && bucket > track.bucket;
  return { track: { ...track, bucket }, refresh: crossed };
}
